"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { SlidersHorizontal, Grid3X3, LayoutGrid, ChevronDown } from "lucide-react";

const categories = [
  { id: "all", label: "全部" },
  { id: "new", label: "新品" },
  { id: "audio", label: "音频" },
  { id: "wearable", label: "穿戴" },
  { id: "home", label: "家居" },
  { id: "camera", label: "摄影" },
];

const sortOptions = [
  { id: "featured", label: "精选推荐" },
  { id: "newest", label: "最新上架" },
  { id: "price-low", label: "价格从低到高" },
  { id: "price-high", label: "价格从高到低" },
];

interface FilterBarProps {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  viewMode: "masonry" | "grid";
  onViewModeChange: (mode: "masonry" | "grid") => void;
}

export default function FilterBar({
  activeCategory,
  onCategoryChange,
  viewMode,
  onViewModeChange,
}: FilterBarProps) {
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [activeSort, setActiveSort] = useState("featured");

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="sticky top-0 z-40 w-full border-b border-[#E2E8F0] bg-[#F8FAFC]/95 backdrop-blur-md"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-8 lg:px-12">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* 左侧分类标签 */}
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => onCategoryChange(category.id)}
                className="group relative whitespace-nowrap px-4 py-2 text-sm transition-colors"
              >
                <span
                  className={`transition-colors duration-300 ${
                    activeCategory === category.id
                      ? "font-medium text-[#3B82F6]"
                      : "text-[#64748B] hover:text-[#3B82F6]"
                  }`}
                >
                  {category.label}
                </span>
                {activeCategory === category.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#3B82F6]"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* 右侧控制区 */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* 排序下拉 */}
            <div className="relative">
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[#64748B] transition-colors hover:bg-white hover:text-[#3B82F6]"
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {sortOptions.find((opt) => opt.id === activeSort)?.label}
                </span>
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${
                    isSortOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* 下拉菜单 */}
              {isSortOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 top-full z-50 mt-2 w-44 rounded-xl border border-[#E2E8F0] bg-white p-1 shadow-lg"
                >
                  {sortOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        setActiveSort(option.id);
                        setIsSortOpen(false);
                      }}
                      className={`w-full rounded-lg px-4 py-2 text-left text-sm transition-colors ${
                        activeSort === option.id
                          ? "bg-[#F1F5F9] font-medium text-[#3B82F6]"
                          : "text-[#64748B] hover:bg-[#F1F5F9]/50"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* 分隔线 */}
            <div className="hidden h-5 w-[1px] bg-[#E2E8F0] sm:block" />

            {/* 视图切换 */}
            <div className="flex items-center rounded-lg bg-white p-1">
              <button
                onClick={() => onViewModeChange("masonry")}
                className={`rounded-md p-2 transition-all ${
                  viewMode === "masonry"
                    ? "bg-[#3B82F6] text-white"
                    : "text-[#94A3B8] hover:text-[#64748B]"
                }`}
                title="瀑布流视图"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => onViewModeChange("grid")}
                className={`rounded-md p-2 transition-all ${
                  viewMode === "grid"
                    ? "bg-[#3B82F6] text-white"
                    : "text-[#94A3B8] hover:text-[#64748B]"
                }`}
                title="网格视图"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 点击外部关闭下拉 */}
      {isSortOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsSortOpen(false)}
        />
      )}
    </motion.div>
  );
}
